import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Moon, Send, Loader2 } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';
import { NOTIFY_KEY } from './EventNotifier';
import { enablePush, disablePush, syncPushPrefs, getPrefs, savePrefs, NOTIFY_CATEGORIES, isIOS, isStandalone } from '../utils/push';
import api from '../api/client';

export function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} aria-pressed={on} className="w-11 h-6 rounded-full relative transition-colors shrink-0"
      style={{ background: on ? 'var(--kmt-gold)' : '#2a2a30' }}>
      <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: on ? 22 : 2 }} />
    </button>
  );
}

function QuietRow({ on, onClick }) {
  const { t } = useI18n();
  const [shift, setShift] = useState(undefined);
  useEffect(() => { api.getMyShift().then(setShift).catch(() => setShift({ shift: null })); }, []);
  const info = shift === undefined ? '' : shift.shift ? `${t('set.quietShift')}: ${shift.shift.split('(')[0].trim()} (${shift.start}–${shift.end})` : t('set.quietNoShift');
  return (
    <div className="flex items-center justify-between gap-3 pt-3" style={{ borderTop: '1px solid var(--kmt-border)' }}>
      <div className="flex gap-2">
        <Moon size={15} style={{ color: 'var(--kmt-gold)', marginTop: 2 }} />
        <div>
          <div className="text-sm">{t('set.quiet')}</div>
          <div className="text-xs" style={{ color: 'var(--kmt-muted)' }} data-testid="quiet-shift-info">{info}</div>
        </div>
      </div>
      <Toggle on={on} onClick={onClick} testid="notify-quiet-toggle" />
    </div>
  );
}

function TestPushButton({ prefs }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await enablePush(prefs);
      const r = await api.pushTest();
      if (r.ok) toast.success(`${t('set.pushTestSent')} (${r.sent}/${r.devices})`);
      else toast.error(`${t('set.pushFailed')}: ${(r.errors || []).join(' · ')}`);
    } catch (e) {
      toast.error(`${t('set.pushFailed')}: ${e.message === 'ios-install' ? t('set.pushIosInstall') : e.message}`);
    } finally { setBusy(false); }
  };
  return (
    <div className="pt-3" style={{ borderTop: '1px solid var(--kmt-border)' }}>
      <button className="kmt-btn-gold flex items-center gap-2 text-xs" disabled={busy} onClick={run} data-testid="push-test-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}{t('set.pushTest')}
      </button>
      <div className="text-xs mt-2" style={{ color: 'var(--kmt-muted)' }}>{t('set.pushTestHint')}</div>
    </div>
  );
}

export default function NotificationPanel() {
  const { t } = useI18n();
  const [notify, setNotify] = useState(() => localStorage.getItem(NOTIFY_KEY) === '1');
  const [prefs, setPrefs] = useState(() => getPrefs());

  const toggleNotify = async () => {
    if (notify) { setNotify(false); localStorage.setItem(NOTIFY_KEY, '0'); disablePush(); toast.success(t('set.notifyOff')); return; }
    if (typeof Notification === 'undefined') { toast.error(t(isIOS() && !isStandalone() ? 'set.pushIosInstall' : 'set.notifyUnsupported')); return; }
    let perm = Notification.permission;
    if (perm === 'default') { try { perm = await Notification.requestPermission(); } catch (e) { perm = 'denied'; } }
    if (perm !== 'granted') { toast.error(t('set.notifyBlocked')); return; }
    setNotify(true); localStorage.setItem(NOTIFY_KEY, '1');
    try { await enablePush(prefs); toast.success(t('set.notifyOn')); }
    catch (e) { toast.error(`${t('set.pushFailed')}: ${e.message === 'ios-install' ? t('set.pushIosInstall') : e.message}`); }
    try { new Notification('KMT Factory', { body: t('set.notifyTest') }); } catch (e) { /* noop */ }
  };

  const toggle = (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); savePrefs(next); syncPushPrefs(next);
  };

  return (
    <div className="space-y-5" data-testid="notification-panel">
      <div className="flex items-center justify-between">
        <div><div className="text-sm font-medium">{t('set.notifications')}</div><div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>{t('set.notificationsDesc')}</div></div>
        <Toggle on={notify} onClick={toggleNotify} testid="notify-master-toggle" />
      </div>
      {notify && (
        <div className="rounded-lg p-3 space-y-3" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid="notify-filters">
          <div className="kmt-label" style={{ fontSize: 9 }}>{t('set.notifyFilters')}</div>
          {NOTIFY_CATEGORIES.map((cat) => (
            <div key={cat} className="flex items-center justify-between">
              <div className="text-sm">{t(`set.notify_${cat}`)}</div>
              <Toggle on={prefs[cat] !== false} onClick={() => toggle(cat)} testid={`notify-filter-${cat}`} />
            </div>
          ))}
          <QuietRow on={!!prefs.quiet} onClick={() => toggle('quiet')} />
          <TestPushButton prefs={prefs} />
        </div>
      )}
    </div>
  );
}
